import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTenant } from '../../context/TenantContext';
import { useRouteMetricsQuery } from '../../services/routing/queries';
import type { RouteMetricsCompanyRow, RouteMetricsDayRow } from '../../types/routing';
import { formatSeconds, newYorkDaysAgo, todayInNewYork } from '../../utils/format';

const DEFAULT_WINDOW_DAYS = 7;

const money = (value: number | null): string => (value == null ? '—' : `$${value.toFixed(2)}`);

const miles = (value: number): string => `${value.toFixed(1)} mi`;

/**
 * Earnings only exist on plans written after amounts were persisted, so a partial count is
 * reported rather than letting an average over three of forty routes read as the whole picture.
 */
const Coverage = ({ covered, total }: { covered: number; total: number }) => {
  if (covered === total) return null;
  return (
    <Typography variant="caption" color="warning.main" sx={{ ml: 0.5 }}>
      ({covered}/{total})
    </Typography>
  );
};

const DayTable = ({ rows }: { rows: RouteMetricsDayRow[] }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>Day</TableCell>
        <TableCell align="right">Routes</TableCell>
        <TableCell align="right">Avg rides / route</TableCell>
        <TableCell align="right">Avg pools / route</TableCell>
        <TableCell align="right">Avg deadhead / route</TableCell>
        <TableCell align="right">Avg earns / route</TableCell>
        <TableCell align="right">Avg route time</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.map(row => (
        <TableRow key={row.day} hover>
          <TableCell>{row.day}</TableCell>
          <TableCell align="right">{row.routes}</TableCell>
          <TableCell align="right">{row.avg_rides.toFixed(2)}</TableCell>
          <TableCell align="right">{row.avg_pools.toFixed(2)}</TableCell>
          <TableCell align="right">{miles(row.avg_deadhead_miles)}</TableCell>
          <TableCell align="right">
            {money(row.avg_amount)}
            <Coverage covered={row.routes_with_amount} total={row.routes} />
          </TableCell>
          <TableCell align="right">{formatSeconds(row.avg_route_seconds) || '—'}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const CompanyTable = ({ rows }: { rows: RouteMetricsCompanyRow[] }) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>Company</TableCell>
        <TableCell align="right">Routes</TableCell>
        <TableCell align="right">Rides</TableCell>
        <TableCell align="right">Avg rides / route</TableCell>
        <TableCell align="right">Deadhead</TableCell>
        <TableCell align="right">Earns</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.map(row => (
        <TableRow key={row.company_id} hover>
          <TableCell>{row.company_id}</TableCell>
          <TableCell align="right">{row.routes}</TableCell>
          <TableCell align="right">{row.rides}</TableCell>
          <TableCell align="right">{row.avg_rides_per_route.toFixed(2)}</TableCell>
          <TableCell align="right">{miles(row.deadhead_miles)}</TableCell>
          <TableCell align="right">
            {money(row.amount)}
            <Coverage covered={row.rides_with_amount} total={row.rides} />
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

export const MetricsPage = () => {
  const { tenant } = useTenant();
  // The endpoint groups by planned New York day, so the window has to be picked in that day too.
  const [from, setFrom] = useState(() => newYorkDaysAgo(DEFAULT_WINDOW_DAYS));
  const [to, setTo] = useState(todayInNewYork);
  const [groupBy, setGroupBy] = useState<'day' | 'company'>('day');

  const { data, isLoading, error } = useRouteMetricsQuery({ tenant, from, to, groupBy });

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={groupBy}
            onChange={(_event, next) => next && setGroupBy(next)}
            aria-label="group by"
          >
            <ToggleButton value="day">By day</ToggleButton>
            <ToggleButton value="company">By company</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            type="date"
            label="From"
            value={from}
            onChange={event => setFrom(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            value={to}
            onChange={event => setTo(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Typography variant="body2" color="text.secondary">
            Averages over planned routes for tenant “{tenant}”. Earnings and the company
            breakdown come from the plan, so they are only recorded for runs planned since that
            was added — a partial count is shown next to any figure that does not cover every
            route.
          </Typography>
        </Stack>
      </Paper>

      {error && <Alert severity="error">{(error as Error).message}</Alert>}
      {isLoading && <CircularProgress aria-label="loading metrics" />}

      {data && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            {groupBy === 'day' ? 'PER PLANNED DAY' : 'PER COMPANY'}
          </Typography>
          {data.rows.length ? (
            <TableContainer sx={{ overflowX: 'auto' }}>
              {data.group_by === 'company' ? (
                <CompanyTable rows={data.rows as RouteMetricsCompanyRow[]} />
              ) : (
                <DayTable rows={data.rows as RouteMetricsDayRow[]} />
              )}
            </TableContainer>
          ) : (
            <Typography color="text.secondary">
              No planned routes in this window for this tenant.
            </Typography>
          )}
        </Paper>
      )}
    </Stack>
  );
};

export default MetricsPage;
